/* eslint-disable  */
import React, { useState, useEffect, useMemo } from 'react';
import GenericModal from '@ui-components/Modal/GenericModal';
import GenericTabs, { TabOption } from '@ui-components/Tabs/GenericTabs';
import GenericInput from '@ui-components/Input/GenericInput';
import GenericButton from '@ui-components/Button/GenericButton';
//import GenericDropdown from '@ui-components/Dropdown/GenericDropdown';
import CustomRadioGroup, { CustomRadioOption } from "@ui-components/RadioGroup/CustomRadioGroup";
import GenericAlert from '@ui-components/Alert/GenericAlert';
import { createBgpNeighbor, updateBgpNeighbor, getBgpNeighborById } from '../../request/request';
import { getInterfacesAddresses } from '../../Cluster/request/request';
import GenericSearchableDropdown from '@ui-components/Dropdown/GenericSearchableDropdown';
import { SkeletonLine } from '@ui-components/Skeleton/GenericSkeleton';
import { useAppSelector } from '@redux/hooks';
import {
  validateNumberField,
  validateHoldTime,
  validateKeepaliveInterval,
  validateConnectRetry,
  validateIPAddress,
  getIpFamily,
  filterAddressOptionsByFamily,
} from '../../../../helpers/bgpValidation';

interface InterfaceAddress {
  id: number;
  ipAddress: string;
}

interface Interface {
  id: number;
  interface: string;
  macAddress: string;
  isPrimary: boolean;
  tags: string;
  discoveredAt: string;
  interfaceAddresses: InterfaceAddress[];
}

const TABS: TabOption[] = [
  { label: 'Basic', value: 'Basic' },
  { label: 'Operating mode', value: 'Operating mode' },
  { label: 'Timers', value: 'Timers' },
  { label: 'Advanced', value: 'Advanced' },
];

interface BgpModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: any) => void;
  mode?: "add" | "edit";
  initialValues?: any;
  reflectorId?: number;
  getAccessTokenSilently: () => Promise<string>;
  context?: 'reflector' | 'cluster';
  clusterId?: number;
  clusterReflectors?: number[];
  clusterInterfaceIds?: number[];
}

const OPERATING_MODE_OPTIONS: CustomRadioOption[] = [
  {
    value: "enterprise",
    label: "Entreprise Mode",
    description: "Optimized for enterprise networks with simplified routing policies and automatic next-hop resolution",
  },
  {
    value: "telco",
    label: "Telco Mode",
    description: "Advanced features for service providers networks, including MPLS support, route distinguishers, and complex policy options.",
  },
];

const BgpModal: React.FC<BgpModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode = "add",
  initialValues = {},
  reflectorId,
  getAccessTokenSilently,
  context,
  clusterId,
  clusterReflectors,
  clusterInterfaceIds,
}) => {
  const { tenantId } = useAppSelector((state: any) => state.selection);
  const [activeTab, setActiveTab] = useState<string>('Basic');
  const [interfaces, setInterfaces] = useState<Interface[]>([]);
  const [selectedInterfaceId, setSelectedInterfaceId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [neighborDetails, setNeighborDetails] = useState<any>(null);
  const [loadingNeighborDetails, setLoadingNeighborDetails] = useState(false);
  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    variant: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    description: "",
    variant: "success"
  });

  // Form fields for Basic tab
  const [neighborAddress, setNeighborAddress] = useState('');
  const [localAddress, setLocalAddress] = useState('');
  const [peerAs, setPeerAs] = useState('');
  const [localAs, setLocalAs] = useState('');
  const [description, setDescription] = useState('');
  const [operatingMode, setOperatingMode] = useState<'enterprise' | 'telco'>('enterprise');
  const [holdTime, setHoldTime] = useState('90');
  const [keepaliveInterval, setKeepaliveInterval] = useState('30');
  const [connectRetry, setConnectRetry] = useState('120');
  const [enableGracefulRestart, setEnableGracefulRestart] = useState(true);
  const [enableAddPath, setEnableAddPath] = useState(false);
  const [enableBfd, setEnableBfd] = useState(true);
  const [enableMultihop, setEnableMultihop] = useState(true);
  // bfdInterval represents the BFD desired_min_tx_interval (what we will
  // transmit). bfdRequiredMinRxInterval is the minimum interval we accept
  // for receiving. On edit, if the backend only has bfdInterval (legacy),
  // we pre-fill the RX input with the same value so the UX matches the old
  // single-field behavior.
  const [bfdInterval, setBfdInterval] = useState('300');
  const [bfdRequiredMinRxInterval, setBfdRequiredMinRxInterval] = useState('300');
  const [bfdMultiplier, setBfdMultiplier] = useState('3');
  const [md5Password, setMd5Password] = useState('');

  // Validation error states
  const [peerAsError, setPeerAsError] = useState('');
  const [localAsError, setLocalAsError] = useState('');
  const [holdTimeError, setHoldTimeError] = useState('');
  const [keepaliveIntervalError, setKeepaliveIntervalError] = useState('');
  const [connectRetryError, setConnectRetryError] = useState('');
  const [bfdIntervalError, setBfdIntervalError] = useState('');
  const [bfdRequiredMinRxIntervalError, setBfdRequiredMinRxIntervalError] = useState('');
  const [bfdMultiplierError, setBfdMultiplierError] = useState('');
  const [neighborAddressError, setNeighborAddressError] = useState('');

  // Generic handler for number fields with validation
  const handleNumberFieldChange = (
    value: string,
    setValue: (value: string) => void,
    setError: (error: string) => void
  ) => {
    const filteredValue = value.replace(/[^0-9]/g, '');
    setValue(filteredValue);
    setError(validateNumberField(filteredValue));
  };

  // Handler for hold time with validation
  const handleHoldTimeChange = (value: string) => {
    const filteredValue = value.replace(/[^0-9]/g, '');
    setHoldTime(filteredValue);
    setHoldTimeError(validateHoldTime(filteredValue));
    // Re-validate keepalive interval when hold time changes
    if (keepaliveInterval) {
      setKeepaliveIntervalError(validateKeepaliveInterval(keepaliveInterval, filteredValue));
    }
  };

  // Handler for keepalive interval with validation
  const handleKeepaliveIntervalChange = (value: string) => {
    const filteredValue = value.replace(/[^0-9]/g, '');
    setKeepaliveInterval(filteredValue);
    setKeepaliveIntervalError(validateKeepaliveInterval(filteredValue, holdTime));
  };

  // Handler for connect retry with validation
  const handleConnectRetryChange = (value: string) => {
    const filteredValue = value.replace(/[^0-9]/g, '');
    setConnectRetry(filteredValue);
    setConnectRetryError(validateConnectRetry(filteredValue));
  };

  // Handler for IP address field (IPv4 or IPv6)
  const handleIPAddressChange = (value: string) => {
    const filteredValue = value.replace(/[^0-9a-fA-F:.]/g, '');
    setNeighborAddress(filteredValue);
    setNeighborAddressError(validateIPAddress(filteredValue));
  };

  // Source address state (standalone)
  const [sourceAddressOptions, setSourceAddressOptions] = useState<Array<{ value: string; label: string; interfaceAddressId: number }>>([]);

  // Source address state (cluster -- one per reflector instance)
  const [sourceAddressOptions1, setSourceAddressOptions1] = useState<Array<{ value: string; label: string; interfaceAddressId: number }>>([]);
  const [sourceAddressOptions2, setSourceAddressOptions2] = useState<Array<{ value: string; label: string; interfaceAddressId: number }>>([]);
  const [selectedInterfaceId1, setSelectedInterfaceId1] = useState<number | null>(null);
  const [selectedInterfaceId2, setSelectedInterfaceId2] = useState<number | null>(null);
  const [localAddress1, setLocalAddress1] = useState('');
  const [localAddress2, setLocalAddress2] = useState('');

  // A BGP session runs over one address family: the source address must match the neighbor's.
  const visibleSourceOptions = useMemo(
    () => filterAddressOptionsByFamily(sourceAddressOptions, neighborAddress),
    [sourceAddressOptions, neighborAddress]
  );
  const visibleSourceOptions1 = useMemo(
    () => filterAddressOptionsByFamily(sourceAddressOptions1, neighborAddress),
    [sourceAddressOptions1, neighborAddress]
  );
  const visibleSourceOptions2 = useMemo(
    () => filterAddressOptionsByFamily(sourceAddressOptions2, neighborAddress),
    [sourceAddressOptions2, neighborAddress]
  );

  // Drop a selected source address once the neighbor address switches to the other family.
  // Only after the options loaded, so an edit does not lose its selection before the fetch returns.
  useEffect(() => {
    const isHidden = (loaded: typeof sourceAddressOptions, visible: typeof sourceAddressOptions, id: number | null) =>
      id !== null && loaded.length > 0 && !visible.some(option => option.interfaceAddressId === id);

    if (isHidden(sourceAddressOptions, visibleSourceOptions, selectedInterfaceId)) {
      setSelectedInterfaceId(null);
      setLocalAddress('');
    }
    if (isHidden(sourceAddressOptions1, visibleSourceOptions1, selectedInterfaceId1)) {
      setSelectedInterfaceId1(null);
      setLocalAddress1('');
    }
    if (isHidden(sourceAddressOptions2, visibleSourceOptions2, selectedInterfaceId2)) {
      setSelectedInterfaceId2(null);
      setLocalAddress2('');
    }
  }, [
    sourceAddressOptions, sourceAddressOptions1, sourceAddressOptions2,
    visibleSourceOptions, visibleSourceOptions1, visibleSourceOptions2,
    selectedInterfaceId, selectedInterfaceId1, selectedInterfaceId2,
  ]);

  // Add validation check
  const isFormValid = () => {
    const commonValidation = 
      neighborAddress.trim() !== '' &&
      peerAs.trim() !== '' &&
      localAs.toString().trim() !== '' &&
      holdTime.trim() !== '' &&
      keepaliveInterval.trim() !== '' &&
      connectRetry.trim() !== '' &&
      bfdInterval.trim() !== '' &&
      bfdRequiredMinRxInterval.trim() !== '' &&
      bfdMultiplier.trim() !== '' &&
      (operatingMode === 'enterprise' || operatingMode === 'telco');
    
    // For cluster mode, check both local addresses - COMMENTED OUT FOR NOW
    /* if (context === 'cluster') {
      const isValid = commonValidation && 
        (mode === "edit" || (localAddress1.trim() !== '' && localAddress2.trim() !== ''));
      
      return isValid;
    }
    
    // For standalone mode
    const isValid = commonValidation && 
      (mode === "edit" || localAddress.trim() !== '');
    
    return isValid; */
    
    // For now, just return common validation since local addresses are not required
    return commonValidation;
  };

  useEffect(() => {
    if (!isOpen || !tenantId) return;

    const mapAddressOptions = (data: any[]) =>
      data
        .filter((addr: any) => addr.ipAddress && addr.ipAddress !== 'none')
        .map((addr: any) => ({
          value: String(addr.interfaceAddressId),
          label: addr.ipAddress,
          interfaceAddressId: addr.interfaceAddressId,
        }));

    if (context === 'cluster' && clusterReflectors && clusterReflectors.length === 2) {
      const fetchBoth = async () => {
        try {
          const token = await getAccessTokenSilently();
          const [res1, res2] = await Promise.all([
            getInterfacesAddresses(token, clusterReflectors[0], tenantId),
            getInterfacesAddresses(token, clusterReflectors[1], tenantId),
          ]);
          setSourceAddressOptions1(mapAddressOptions(res1.data?.data || []));
          setSourceAddressOptions2(mapAddressOptions(res2.data?.data || []));
        } catch (err) {
          console.error('Failed to load source addresses for cluster:', err);
        }
      };
      fetchBoth();
    } else if (reflectorId) {
      const fetchSingle = async () => {
        try {
          const token = await getAccessTokenSilently();
          const res = await getInterfacesAddresses(token, reflectorId, tenantId);
          setSourceAddressOptions(mapAddressOptions(res.data?.data || []));
        } catch (err) {
          console.error('Failed to load source addresses:', err);
        }
      };
      fetchSingle();
    }
  }, [isOpen, reflectorId, clusterReflectors, context, tenantId]);

  // Get all IP addresses from all interfaces
  const allIpAddresses = interfaces.flatMap(iface => 
    iface.interfaceAddresses.map(addr => ({
      id: addr.id,
      ipAddress: addr.ipAddress
    }))
  );

  const handleInterfaceSelect = (ipAddress: string) => {
    const selectedAddress = allIpAddresses.find(addr => addr.ipAddress === ipAddress);
    if (selectedAddress) {
      setSelectedInterfaceId(selectedAddress.id);
      setLocalAddress(ipAddress);
    }
  };

  // Fetch neighbor details when in edit mode
  useEffect(() => {
    const fetchNeighborDetails = async () => {
      if (mode === "edit" && initialValues?.id && isOpen && tenantId) {
        setLoadingNeighborDetails(true);
        try {
          const token = await getAccessTokenSilently();
          const response = await getBgpNeighborById(token, Number(initialValues.id), tenantId);
          setNeighborDetails(response.data);
        } catch (err) {
          console.error('Error fetching BGP neighbor details:', err);
          setError('Failed to load BGP neighbor details');
          setNeighborDetails(null);
        } finally {
          setLoadingNeighborDetails(false);
        }
      } else if (mode === "add" || !isOpen) {
        // Reset when switching to add mode or closing modal
        setNeighborDetails(null);
      }
    };

    fetchNeighborDetails();
  }, [mode, initialValues?.id, isOpen, tenantId, getAccessTokenSilently]);

  // Populate form fields when editing
  useEffect(() => {
    // Use neighborDetails if available (from API), otherwise fall back to initialValues
    // Handle both flat structure and nested structure (base_config)
    let values = neighborDetails || initialValues;
    
    // If API returns nested structure with base_config, extract it
    if (values?.base_config) {
      values = { ...values.base_config, ...values };
    }
    
    if (mode === "edit" && values) {
      const neighborAddressValue = values.neighborAddress || '';
      setNeighborAddress(neighborAddressValue);
      setNeighborAddressError(validateIPAddress(neighborAddressValue));
      setLocalAddress(values.localAddress || values.interfaceAddress?.ipAddress || '');
      setSelectedInterfaceId(values.interfaceAddressId || values.interfaceAddress?.id || null);

      if (context === 'cluster' && values.interfaces && Array.isArray(values.interfaces)) {
        if (values.interfaces[0]?.id) setSelectedInterfaceId1(values.interfaces[0].id);
        if (values.interfaces[1]?.id) setSelectedInterfaceId2(values.interfaces[1].id);
      }
      const peerAsValue = values.peerAs?.toString() || '';
      const localAsValue = values.localAs?.toString() || '';
      const holdTimeValue = values.holdTime?.toString() || '90';
      const keepaliveIntervalValue = values.keepAliveInterval?.toString() || values.keepaliveInterval?.toString() || '30';
      const connectRetryValue = values.connectRetry?.toString() || '120';
      const bfdIntervalValue = values.bfdInterval?.toString() || '300';
      // Retro-compat: when the backend doesn't ship bfdRequiredMinRxInterval
      // (or ships it null), pre-fill the RX input with bfdInterval so the
      // user sees the same value they would have seen before the split.
      const bfdRequiredMinRxIntervalValue =
        values.bfdRequiredMinRxInterval != null
          ? values.bfdRequiredMinRxInterval.toString()
          : bfdIntervalValue;
      const bfdMultiplierValue = values.bfdMultiplier?.toString() || '3';
      
      setPeerAs(peerAsValue);
      setLocalAs(localAsValue);
      setDescription(values.description || '');
      setOperatingMode((values.operatingMode || 'enterprise').toLowerCase());
      setHoldTime(holdTimeValue);
      setKeepaliveInterval(keepaliveIntervalValue);
      setConnectRetry(connectRetryValue);
      // Explicitly handle boolean values - ensure they're properly set
      setEnableGracefulRestart(values.gracefulRestart !== undefined ? Boolean(values.gracefulRestart) : true);
      setEnableAddPath(values.addPath !== undefined ? Boolean(values.addPath) : false);
      setEnableBfd(values.BFD !== undefined ? Boolean(values.BFD) : true);
      setEnableMultihop(values.multihop !== undefined ? Boolean(values.multihop) : false);
      setBfdInterval(bfdIntervalValue);
      setBfdRequiredMinRxInterval(bfdRequiredMinRxIntervalValue);
      setBfdMultiplier(bfdMultiplierValue);
      // Check both 'password' and 'md5Password' field names (API uses 'password')
      setMd5Password(values.password || values.md5Password || '');
      
      // Validate initial values
      setPeerAsError(validateNumberField(peerAsValue));
      setLocalAsError(validateNumberField(localAsValue));
      setHoldTimeError(validateHoldTime(holdTimeValue));
      setKeepaliveIntervalError(validateKeepaliveInterval(keepaliveIntervalValue, holdTimeValue));
      setConnectRetryError(validateConnectRetry(connectRetryValue));
      setBfdIntervalError(validateNumberField(bfdIntervalValue));
      setBfdRequiredMinRxIntervalError(validateNumberField(bfdRequiredMinRxIntervalValue));
      setBfdMultiplierError(validateNumberField(bfdMultiplierValue));
    } else if (mode === "add") {
      setNeighborAddress('');
      setLocalAddress('');
      setSelectedInterfaceId(null);
      setSelectedInterfaceId1(null);
      setSelectedInterfaceId2(null);
      setLocalAddress1('');
      setLocalAddress2('');
      setPeerAs('');
      setLocalAs('');
      setDescription('');
      setOperatingMode('enterprise');
      setHoldTime('90');
      setKeepaliveInterval('30');
      setConnectRetry('120');
      setEnableGracefulRestart(true);
      setEnableAddPath(false);
      setEnableBfd(true);
      setEnableMultihop(true);
      setBfdInterval('300');
      setBfdRequiredMinRxInterval('300');
      setBfdMultiplier('3');
      setMd5Password('');
    }
  }, [mode, initialValues, neighborDetails, isOpen]);

  // Re-validate keepalive interval when hold time changes
  useEffect(() => {
    if (keepaliveInterval) {
      setKeepaliveIntervalError(validateKeepaliveInterval(keepaliveInterval, holdTime));
    }
  }, [holdTime]);

  // Sync multihop with BFD: when BFD is enabled, enable and set multihop to true; when disabled, set to false
  useEffect(() => {
    if (enableBfd) {
      setEnableMultihop(true);
    } else {
      setEnableMultihop(false);
    }
  }, [enableBfd]);

  const handleSubmit = async () => {
    const contextType = context || 'reflector';
    // Use neighborDetails ID if available (from API), otherwise use initialValues
    // Handle both flat and nested structures
    let neighborId = neighborDetails?.id || initialValues?.id;
    // If neighborDetails has nested structure, try to get ID from ctxr_config
    if (!neighborId && neighborDetails?.ctxr_config && Array.isArray(neighborDetails.ctxr_config) && neighborDetails.ctxr_config.length > 0) {
      neighborId = neighborDetails.ctxr_config[0]?.bgpNeighborId || neighborDetails.ctxr_config[0]?.neighborId;
    }
    const isEditMode = mode === "edit" && neighborId;

    // The field accepts partial input while typing; only a complete address can be sent.
    if (!getIpFamily(neighborAddress)) {
      setNeighborAddressError("Enter a complete IPv4 or IPv6 address");
      return;
    }

    // Check for validation errors before submitting
    if (neighborAddressError || peerAsError || localAsError || holdTimeError || keepaliveIntervalError || 
        connectRetryError || bfdIntervalError || bfdRequiredMinRxIntervalError || bfdMultiplierError) {
      return; // Don't submit if there are validation errors
    }

    // Build the base config
    const baseConfig = {
      neighborAddress,
      interfaceAddressId: selectedInterfaceId || null,
      peerAs: Number(peerAs),
      localAs: Number(localAs),
      description: description || '',
      operatingMode: operatingMode.toUpperCase(),
      holdTime: Number(holdTime),
      keepAliveInterval: Number(keepaliveInterval),
      connectRetry: Number(connectRetry),
      gracefulRestart: Boolean(enableGracefulRestart), // Explicitly convert to boolean
      addPath: Boolean(enableAddPath), // Explicitly convert to boolean
      BFD: Boolean(enableBfd), // Explicitly convert to boolean
      multihop: Boolean(enableMultihop), // Explicitly convert to boolean
      // bfdInterval == BFD desired_min_tx_interval. Kept as the legacy name
      // so older backends that only know this field keep working.
      bfdInterval: Number(bfdInterval),
      // bfdRequiredMinRxInterval == BFD required_min_rx_interval. Sent as a
      // separate field; backends that haven't been updated yet will ignore
      // it and fall back to bfdInterval on their side.
      bfdRequiredMinRxInterval: Number(bfdRequiredMinRxInterval),
      bfdMultiplier: Number(bfdMultiplier),
      password: md5Password || null,
    };

    let payload;
    let targetId;

    if (
      contextType === 'cluster' &&
      clusterReflectors &&
      clusterReflectors.length === 2
      // && selectedInterfaceId1 && selectedInterfaceId2 // COMMENTED OUT - no longer sending local addresses
    ) {
      // Cluster: send array for ctxr_config
      // Map each reflector instance to its correct neighborId from the reflectors array
      // In cluster mode, each reflector instance has its own bgpNeighborId
      const ctxrConfig = clusterReflectors.map((reflectorInstanceId) => {
        let bgpNeighborId = null;
        
        if (isEditMode) {
          // First, try to get from initialValues.reflectors (from list API - has individual IDs per reflector)
          if (initialValues?.reflectors && Array.isArray(initialValues.reflectors)) {
            const reflector = initialValues.reflectors.find(
              (r: any) => r.instanceId === reflectorInstanceId
            );
            if (reflector && (reflector.neighborId || reflector.bgpNeighborId)) {
              bgpNeighborId = reflector.neighborId || reflector.bgpNeighborId;
            }
          }
          
          // Fallback: check neighborDetails.reflectors (if API returns it)
          if (!bgpNeighborId && neighborDetails?.reflectors && Array.isArray(neighborDetails.reflectors)) {
            const reflector = neighborDetails.reflectors.find(
              (r: any) => r.instanceId === reflectorInstanceId || r.reflectorInstanceId === reflectorInstanceId
            );
            if (reflector && (reflector.neighborId || reflector.bgpNeighborId)) {
              bgpNeighborId = reflector.neighborId || reflector.bgpNeighborId;
            }
          }
          
          // Final fallback: use neighborIds array if available (indexed by reflector position)
          if (!bgpNeighborId && initialValues?.neighborIds && Array.isArray(initialValues.neighborIds)) {
            const reflectorIndex = clusterReflectors.indexOf(reflectorInstanceId);
            if (reflectorIndex >= 0 && initialValues.neighborIds[reflectorIndex]) {
              bgpNeighborId = initialValues.neighborIds[reflectorIndex];
            }
          }
          
          // Last resort: use the main id if we can't find individual IDs
          if (!bgpNeighborId) {
            bgpNeighborId = neighborDetails?.id || initialValues?.id;
          }
        }
        
        const reflectorIndex = clusterReflectors.indexOf(reflectorInstanceId);
        const ifaceId = reflectorIndex === 0 ? selectedInterfaceId1 : selectedInterfaceId2;

        return {
          reflectorInstanceId,
          bgpNeighborId: bgpNeighborId || null,
          interfaceAddressId: ifaceId || null
        };
      });
      
      // Validate that all reflectors have bgpNeighborId in edit mode
      if (isEditMode) {
        const missingIds = ctxrConfig
          .filter((item: any) => !item.bgpNeighborId)
          .map((item: any) => item.reflectorInstanceId);
        
        if (missingIds.length > 0) {
          console.error('Missing bgpNeighborId for reflector instances:', missingIds);
          setAlertState({
            isOpen: true,
            title: "Error",
            description: `Cannot update: Missing BGP neighbor ID for reflector instances ${missingIds.join(', ')}. Please refresh and try again.`,
            variant: "error"
          });
          setIsSubmitting(false);
          return;
        }
      }
      
      payload = {
        base_config: baseConfig,
        ctxr_config: ctxrConfig
      };
      targetId = clusterId;
    } else {
      // Standalone: send single object
      payload = {
        base_config: baseConfig,
        ctxr_config: {
          reflectorInstanceId: reflectorId as number,
          bgpNeighborId: isEditMode ? neighborId : undefined,
          interfaceAddressId: selectedInterfaceId || null
        }
      };
      targetId = reflectorId as number;
    }

    setIsSubmitting(true);
    try {
      if (!tenantId) return;
      const token = await getAccessTokenSilently();
      if(targetId){
        if (isEditMode) {
          // Update existing BGP neighbor
          await updateBgpNeighbor(token, targetId, payload as any, tenantId);
          setAlertState({
            isOpen: true,
            title: "Success",
            description: "BGP neighbor has been updated successfully.",
            variant: "success"
          });
        } else {
          // Create new BGP neighbor
          await createBgpNeighbor(token, targetId, payload as any, tenantId);
          setAlertState({
            isOpen: true,
            title: "Success",
            description: "BGP neighbor has been created successfully.",
            variant: "success"
          });
        }
      }
      onClose();
      if (onSubmit) {
         onSubmit(payload);
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} BGP neighbor:`, error);
      setAlertState({
        isOpen: true,
        title: "Error",
        description: `Failed to ${isEditMode ? 'update' : 'create'} BGP neighbor. Please try again.`,
        variant: "error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseAlert = () => {
    setAlertState(prev => ({ ...prev, isOpen: false }));
  };

  const holdTimeNum = Number(holdTime) || 0;
  const recommendedKeepalive = Math.round(holdTimeNum / 3);
  const recommendedHold = 90;
  const recommendedConnectRetry = 120;

  return (
    <>
      <GenericModal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        title={
          <span className="flex items-center gap-2 font-semibold">
            {mode === "edit" ? "Edit BGP neighbor" : "Add BGP neighbor"}
          </span>
        }
      >
        <div className="flex flex-col items-center p-6 pt-2 gap-4 min-h-[400px] text-body">
        {(loading || loadingNeighborDetails) ? (
          <div className="flex flex-col gap-6 w-full">
            {/* Tabs skeleton */}
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((i) => (
                <SkeletonLine key={i} w={72} h={36} className="rounded" />
              ))}
            </div>
            {/* Form fields skeleton */}
            <div className="flex flex-col gap-5 w-full">
              <div>
                <SkeletonLine w="30%" h={14} className="rounded mb-2" />
                <SkeletonLine w="100%" h={40} className="rounded" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <SkeletonLine w="40%" h={14} className="rounded mb-2" />
                  <SkeletonLine w="100%" h={40} className="rounded" />
                </div>
                <div>
                  <SkeletonLine w="40%" h={14} className="rounded mb-2" />
                  <SkeletonLine w="100%" h={40} className="rounded" />
                </div>
              </div>
              <div>
                <SkeletonLine w="25%" h={14} className="rounded mb-2" />
                <SkeletonLine w="100%" h={40} className="rounded" />
              </div>
              <div>
                <SkeletonLine w="35%" h={14} className="rounded mb-2" />
                <SkeletonLine w="100%" h={40} className="rounded" />
              </div>
            </div>
          </div>
        ) : (
        <>
          {/* Tabs */}
          <div className="w-full ">
            <GenericTabs
              tabs={TABS}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              className='px-0'
            />
          </div>
          {/* Tab Content */}
          {activeTab === 'Basic' && (
            <div className="flex flex-col gap-6 w-full">
              <div className="grid grid-cols-1 gap-2">
                {/* Neighbor Address */}
                <div className="w-full">
                  <GenericInput
                    label="Neighbor address"
                    value={neighborAddress}
                    onChange={handleIPAddressChange}
                    type="text"
                    required
                    hasError={!!neighborAddressError}
                    helperText={neighborAddressError || "IPv4 or IPv6 address of the BGP peer"}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {/* Peer AS */}
                <div className="w-full">
                  <GenericInput
                    label="Peer AS"
                    value={peerAs}
                    onChange={(v) => handleNumberFieldChange(v, setPeerAs, setPeerAsError)}
                    type="text"
                    required
                    hasError={!!peerAsError}
                    helperText={peerAsError || "Autonomous System number of the peer"}
                  />
                </div>
                {/* Local AS */}
                <div className="w-full">
                  <GenericInput
                    label="Local AS"
                    value={localAs}
                    onChange={(v) => handleNumberFieldChange(v, setLocalAs, setLocalAsError)}
                    type="text"
                    required
                    hasError={!!localAsError}
                    helperText={localAsError || "Local Autonomous System number for this session"}
                  />
                </div>
              </div>
              {/* Description */}
              <GenericInput
                label="Description"
                value={description}
                onChange={setDescription}
                type="text"
              />
              <div className="text-xs text-semantic-text-secondary ">Optional description for this BGP session</div>

              {/* Source Address */}
              <div className="text-sm font-medium text-semantic-text-primary mb-1">Source Address</div>
              {context === 'cluster' && clusterReflectors && clusterReflectors.length === 2 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="w-full">
                    <GenericSearchableDropdown
                      items={visibleSourceOptions1}
                      value={selectedInterfaceId1 ? String(selectedInterfaceId1) : ''}
                      onSelect={(v) => {
                        const opt = visibleSourceOptions1.find(o => o.value === v);
                        setSelectedInterfaceId1(opt ? opt.interfaceAddressId : null);
                        setLocalAddress1(opt ? opt.label : '');
                      }}
                      defaultLabel="Primary"
                      widthClass="w-full"
                      maxWidthClass="max-w-full"
                      emptyItemsMessage="No addresses available"
                      allowCustomValues={false}
                    />
                  </div>
                  <div className="w-full">
                    <GenericSearchableDropdown
                      items={visibleSourceOptions2}
                      value={selectedInterfaceId2 ? String(selectedInterfaceId2) : ''}
                      onSelect={(v) => {
                        const opt = visibleSourceOptions2.find(o => o.value === v);
                        setSelectedInterfaceId2(opt ? opt.interfaceAddressId : null);
                        setLocalAddress2(opt ? opt.label : '');
                      }}
                      defaultLabel="Secondary"
                      widthClass="w-full"
                      maxWidthClass="max-w-full"
                      emptyItemsMessage="No addresses available"
                      allowCustomValues={false}
                    />
                  </div>
                </div>
              ) : (
                <GenericSearchableDropdown
                  items={visibleSourceOptions}
                  value={selectedInterfaceId ? String(selectedInterfaceId) : ''}
                  onSelect={(v) => {
                    const opt = visibleSourceOptions.find(o => o.value === v);
                    setSelectedInterfaceId(opt ? opt.interfaceAddressId : null);
                    setLocalAddress(opt ? opt.label : '');
                  }}
                  defaultLabel="Select source address"
                  widthClass="w-full"
                  maxWidthClass="max-w-full"
                  emptyItemsMessage="No addresses available"
                  allowCustomValues={false}
                />
              )}
              <div className="text-xs text-semantic-text-secondary mt-1">Optional. Local IP used as BGP update-source.</div>
            </div>
          )}
          {activeTab === 'Operating mode' && (
            <div className="w-full">
              <div className="font-semibold mb-4 text-semantic-text-primary">Select mode</div>
              <CustomRadioGroup
                name="operatingMode"
                options={OPERATING_MODE_OPTIONS}
                value={operatingMode}
                onChange={v => setOperatingMode(v as 'enterprise' | 'telco')}
              />
            </div>
          )}
          {activeTab === 'Timers' && (
            <div className="w-full flex flex-col gap-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                {/* Hold time */}
                <div>
                  <GenericInput
                    label={
                      <span>
                        Hold time
                      </span>
                    }
                    value={holdTime}
                    onChange={handleHoldTimeChange}
                    type="text"
                    required
                    hasError={!!holdTimeError}
                    helperText={holdTimeError}
                    unit="seconds"
                  />
                </div>
                {/* Keepalive interval */}
                <div>
                  <GenericInput
                    label={
                      <span>
                        Keepalive interval 
                      </span>
                    }
                    value={keepaliveInterval}
                    onChange={handleKeepaliveIntervalChange}
                    type="text"
                    required
                    hasError={!!keepaliveIntervalError}
                    helperText={keepaliveIntervalError}
                    unit="seconds"
                  />
                </div>
                {/* Connect retry: full row on mobile, half-width spanning both cols on md+ */}
                <div className="md:col-span-2 md:w-1/2">
                  <GenericInput
                    label={
                      <span>
                        Connect retry 
                      </span>
                    }
                    value={connectRetry}
                    onChange={handleConnectRetryChange}
                    type="text"
                    required
                    hasError={!!connectRetryError}
                    helperText={connectRetryError}
                    unit="seconds"
                  />
                </div>
              </div>
              {/* Recommended settings box */}
              <div className="rounded-md bg-semantic-bg-secondary-alt border-semantic-border-secondary px-4 py-3 text-semantic-text-secondary">
                <div className="font-semibold text-semantic-text-secondary mb-1">Recommended timer settings:</div>
                <div>Hold time: {recommendedHold} seconds.</div>
                <div>Keepalive interval: {recommendedKeepalive} seconds (1/3 of hold time).</div>
                <div>Connect retry: {recommendedConnectRetry} seconds</div>
              </div>
            </div>
          )}
          {activeTab === 'Advanced' && (
            <div className="w-full flex flex-col gap-6">
              {/* Checkboxes */}
              <div className="flex flex-col gap-3">
                <label className="flex items-center gap-2 cursor-pointer text-body">
                  <input
                    type="checkbox"
                    checked={enableGracefulRestart}
                    onChange={(e) => setEnableGracefulRestart(e.target.checked)}
                    className="accent-[var(--color-semantic-bg-brand-bold,#197278)] w-5 h-5 rounded border border-[var(--color-semantic-border-secondary,#DCDDE0)]"
                  />
                  <span className="text-semantic-text-primary">
                    Enable graceful restart
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-body">
                  <input
                    type="checkbox"
                    checked={enableAddPath}
                    onChange={(e) => setEnableAddPath(e.target.checked)}
                    className="accent-[var(--color-semantic-bg-brand-bold,#197278)] w-5 h-5 rounded-sm border border-[var(--color-semantic-border-secondary,#DCDDE0)]"
                  />
                  <span className="text-semantic-text-primary">
                    Enable add-path
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-body">
                  <input
                    type="checkbox"
                    checked={enableBfd}
                    onChange={(e) => setEnableBfd(e.target.checked)}
                    className="accent-[var(--color-semantic-bg-brand-bold,#197278)] w-5 h-5 rounded border border-[var(--color-semantic-border-secondary,#DCDDE0)]"
                  />
                  <span className="text-semantic-text-primary">
                    Enable BFD
                  </span>
                </label>
                <label className="flex items-center gap-2 text-body">
                  <input
                    type="checkbox"
                    checked={enableMultihop}
                    onChange={(e) => setEnableMultihop(e.target.checked)}
                    disabled={!enableBfd}
                    className="accent-[var(--color-semantic-bg-brand-bold,#197278)] w-5 h-5 rounded border border-[var(--color-semantic-border-secondary,#DCDDE0)] disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <span className={!enableBfd ? "text-semantic-text-secondary" : "text-semantic-text-primary"}>
                    Enable multihop
                  </span>
                </label>
              </div>
              {/* BFD Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
                <div>
                  <GenericInput
                    label="BFD Desired Min TX"
                    tooltipText="desired_min_tx_interval — how often this side wants to transmit BFD control packets (ms)."
                    value={bfdInterval}
                    onChange={(v) => handleNumberFieldChange(v, setBfdInterval, setBfdIntervalError)}
                    type="text"
                    required
                    hasError={!!bfdIntervalError}
                    helperText={bfdIntervalError}
                    unit="ms"
                  />
                </div>
                <div>
                  <GenericInput
                    label="BFD Required Min RX"
                    tooltipText="required_min_rx_interval — minimum interval this side accepts for receiving BFD packets (ms). Lower = faster failure detection, higher = more tolerant."
                    value={bfdRequiredMinRxInterval}
                    onChange={(v) => handleNumberFieldChange(v, setBfdRequiredMinRxInterval, setBfdRequiredMinRxIntervalError)}
                    type="text"
                    required
                    hasError={!!bfdRequiredMinRxIntervalError}
                    helperText={bfdRequiredMinRxIntervalError}
                    unit="ms"
                  />
                </div>
                <div>
                  <GenericInput
                    label="BFD Detect Multiplier"
                    tooltipText="detect_multiplier — number of consecutive missed BFD packets before declaring the session DOWN."
                    value={bfdMultiplier}
                    onChange={(v) => handleNumberFieldChange(v, setBfdMultiplier, setBfdMultiplierError)}
                    type="text"
                    required
                    hasError={!!bfdMultiplierError}
                    helperText={bfdMultiplierError}
                  />
                </div>
              </div>
              {/* MD5 Password */}
              <div>
                <GenericInput
                  label="MD5 password"
                  value={md5Password}
                  onChange={setMd5Password}
                  type="password"
                  helperText="Optional password"
                />
              </div>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex justify-end gap-4 mt-6 w-full">
            <GenericButton label="Cancel" variant="secondary" onClick={onClose} disabled={isSubmitting} />
            <GenericButton
              label={isSubmitting ? "Saving..." : "Save"}
              variant="brand"
              onClick={handleSubmit}
              disabled={!isFormValid() || isSubmitting}
              loading={isSubmitting}
            />
          </div>
        </>
        )}
        </div>
      </GenericModal>

      <GenericAlert
        isOpen={alertState.isOpen}
        onClose={handleCloseAlert}
        onConfirm={handleCloseAlert}
        title={alertState.title}
        description={alertState.description}
        confirmLabel="OK"
        variant={alertState.variant}
      />
    </>
  );
};

export default BgpModal;
